// Import billing types
import type {
  BillingResource,
  BillingSearchQuery,
  BillingStatsResource,
  CreateInvoiceRequest,
  CreatePaymentMethodRequest,
  InvoiceResource,
  InvoiceSearchQuery,
  PaymentMethodResource,
  ProcessPaymentRequest,
  TransactionResource,
  TransactionSearchQuery,
  TransactionStatsResource,
  UpdateInvoiceRequest,
  UpdatePaymentMethodRequest,
} from '@interfaces/billing/billing.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';
import axios from 'axios';

// Invoice detail interface for JSON endpoint
export interface InvoiceDetailItem {
  id: number;
  description: string;
  currency_code: string;
  currency_symbol: string;
  quantity: number;
  unit_price: string;
  display_unit_price: string;
  tax_rate_percent: number;
  total_price: string;
  display_total_price: string;
  tax_amount: string;
  display_tax_amount: string;
  grand_total_price: string;
  display_grand_total_price: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceDetail {
  uuid: string;
  invoice_number: string;
  currency_code: string;
  currency_symbol: string;
  issue_date: string;
  due_date: string;
  reference_number?: string;
  notes_to_recipient?: string;
  internal_notes?: string;
  total_amount: string;
  display_total_amount: string;
  tax_amount: string;
  display_tax_amount: string;
  grand_total_amount: string;
  display_grand_total_amount: string;
  created_at: string;
  updated_at: string;
  issuing_company_slug: string;
  recipient_company_slug: string;
  invoice_status_type: {
    id: number;
    slug: string;
    name: string;
    label: string;
    description: string;
    enabled: boolean;
  };
  currency: {
    uuid: string;
    code: string;
    name: string;
  };
  payment_term?: {
    uuid: string;
    name: string;
    slug: string;
    days: number;
    enabled: boolean;
  };
  items: InvoiceDetailItem[];
}

export interface BillingReducerState {
  // Billing management
  billingAccounts: Record<string, BillingResource>;
  billingStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  billingError: string | null;
  billingStats: BillingStatsResource | null;

  // Invoice management
  invoices: Record<string, InvoiceResource>;
  invoiceStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  invoiceError: string | null;

  // Invoice detail view
  invoiceDetail: InvoiceDetail | null;
  invoiceDetailStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  invoiceDetailError: string | null;

  // Transaction management
  transactions: Record<string, TransactionResource>;
  transactionStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  transactionError: string | null;
  transactionStats: TransactionStatsResource | null;

  // Payment method management
  paymentMethods: Record<string, PaymentMethodResource>;
  paymentMethodStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  paymentMethodError: string | null;

  // Search queries and pagination
  lastBillingQuery: BillingSearchQuery | null;
  lastInvoiceQuery: InvoiceSearchQuery | null;
  lastTransactionQuery: TransactionSearchQuery | null;

  // General billing state
  isInitialized: boolean;
}

const initialState: BillingReducerState = {
  billingAccounts: {},
  billingStatus: 'idle',
  billingError: null,
  billingStats: null,
  invoices: {},
  invoiceStatus: 'idle',
  invoiceError: null,
  invoiceDetail: null,
  invoiceDetailStatus: 'idle',
  invoiceDetailError: null,
  transactions: {},
  transactionStatus: 'idle',
  transactionError: null,
  transactionStats: null,
  paymentMethods: {},
  paymentMethodStatus: 'idle',
  paymentMethodError: null,
  lastBillingQuery: null,
  lastInvoiceQuery: null,
  lastTransactionQuery: null,
  isInitialized: false,
};

// Billing endpoints
export const getBilling = thunkGet.compUrl('billing', '/c/{companySlug}/admin/billing');

export const updateBilling = thunkPut.compUrl<{ billingData: Partial<BillingResource> }>(
  'billing',
  '/c/{companySlug}/admin/billing',
);

export const getBillingStats = thunkGet.compUrl<{ from?: string; to?: string }>(
  'billing',
  '/c/{companySlug}/admin/billing/stats',
);

export const getBillingHistory = thunkGet.compUrl<BillingSearchQuery>(
  'billing',
  '/c/{companySlug}/admin/billing/history',
);

export const exportBillingData = thunkPost.compUrl<{ format: 'csv' | 'pdf'; from?: string; to?: string }>(
  'billing',
  '/c/{companySlug}/admin/billing/export',
);

// Invoice endpoints
export const getInvoices = thunkGet.compUrl<InvoiceSearchQuery>('billing', '/c/{companySlug}/admin/invoice');

// Alternative invoice endpoint with different response format
// This endpoint returns data directly in response.data instead of response.data.data
export const getInvoicesShow = createAsyncThunk('billing/getInvoicesShow', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState() as AppState;
    const companySlug = state.me?.currentCompanySlug;

    if (!companySlug) {
      throw new Error('No company slug available');
    }

    const response = await axios.get(`/api/v1/c/${companySlug}/admin/invoice/show`);

    // Return the data directly since this endpoint returns { data: [...] }
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: { message?: string }; status?: number }; message?: string };
    return rejectWithValue({
      error: {
        message: axiosError.response?.data?.message || axiosError.message || 'Failed to fetch invoices',
        statusCode: axiosError.response?.status || 500,
        data: axiosError.response?.data,
      },
    });
  }
});

export const createInvoice = thunkPost.compUrl<CreateInvoiceRequest>('billing', '/c/{companySlug}/admin/invoice');

export const getInvoice = thunkGet.compUrl('billing', '/c/{companySlug}/admin/invoice/{invoice}');

export const updateInvoice = thunkPut.compUrl<UpdateInvoiceRequest>(
  'billing',
  '/c/{companySlug}/admin/invoice/{invoice}',
);

export const deleteInvoice = thunkDelete.compUrl('billing', '/c/{companySlug}/admin/invoice/{invoice}');

export const sendInvoice = thunkPost.compUrl<{ email?: string }>(
  'billing',
  '/c/{companySlug}/admin/invoice/{invoice}/send',
);

export const markInvoicePaid = thunkPost.compUrl<{ paidAt?: string }>(
  'billing',
  '/c/{companySlug}/admin/invoice/{invoice}/mark-paid',
);

export const downloadInvoice = thunkGet.compUrl<{ format?: 'pdf' | 'html' }>(
  'billing',
  '/c/{companySlug}/admin/invoice/{invoice}/download',
);

// Fetch invoice detail JSON
export const getInvoiceDetail = createAsyncThunk(
  'billing/getInvoiceDetail',
  async (invoiceUuid: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const companySlug = state.me?.currentCompanySlug;

      if (!companySlug) {
        throw new Error('No company slug available');
      }

      const response = await axios.get(`/api/v1/c/${companySlug}/admin/invoice/view/json/${invoiceUuid}`);

      return response.data.data as InvoiceDetail;
    } catch (error) {
      const axiosError = error as { response?: { data?: { message?: string }; status?: number }; message?: string };
      return rejectWithValue({
        error: {
          message: axiosError.response?.data?.message || axiosError.message || 'Failed to fetch invoice details',
          statusCode: axiosError.response?.status || 500,
          data: axiosError.response?.data,
        },
      });
    }
  },
);

// Transaction endpoints
export const getTransactions = thunkGet.compUrl<TransactionSearchQuery>(
  'billing',
  '/c/{companySlug}/admin/transaction',
);

export const createTransaction = thunkPost.compUrl<{ transactionData: Partial<TransactionResource> }>(
  'billing',
  '/c/{companySlug}/admin/transaction',
);

export const getTransaction = thunkGet.compUrl('billing', '/c/{companySlug}/admin/transaction/{transaction}');

export const updateTransaction = thunkPut.compUrl<{ transactionData: Partial<TransactionResource> }>(
  'billing',
  '/c/{companySlug}/admin/transaction/{transaction}',
);

export const deleteTransaction = thunkDelete.compUrl('billing', '/c/{companySlug}/admin/transaction/{transaction}');

export const refundTransaction = thunkPost.compUrl<{ amount?: number; reason?: string }>(
  'billing',
  '/c/{companySlug}/admin/transaction/{transaction}/refund',
);

export const getTransactionStats = thunkGet.compUrl<{ from?: string; to?: string }>(
  'billing',
  '/c/{companySlug}/admin/transaction/stats',
);

// Payment processing endpoints
export const processPayment = thunkPost.compUrl<ProcessPaymentRequest>(
  'billing',
  '/c/{companySlug}/admin/payment/process',
);

export const getPaymentMethods = thunkGet.compUrl('billing', '/c/{companySlug}/admin/payment/methods');

export const createPaymentMethod = thunkPost.compUrl<CreatePaymentMethodRequest>(
  'billing',
  '/c/{companySlug}/admin/payment/methods',
);

export const getPaymentMethod = thunkGet.compUrl('billing', '/c/{companySlug}/admin/payment/methods/{method}');

export const updatePaymentMethod = thunkPut.compUrl<UpdatePaymentMethodRequest>(
  'billing',
  '/c/{companySlug}/admin/payment/methods/{method}',
);

export const deletePaymentMethod = thunkDelete.compUrl('billing', '/c/{companySlug}/admin/payment/methods/{method}');

export const setDefaultPaymentMethod = thunkPost.compUrl(
  'billing',
  '/c/{companySlug}/admin/payment/methods/{method}/set-default',
);

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearBillingState: (state) => {
      state.billingAccounts = {};
      state.billingStats = null;
      state.invoices = {};
      state.transactions = {};
      state.transactionStats = null;
      state.paymentMethods = {};
      state.lastBillingQuery = null;
      state.lastInvoiceQuery = null;
      state.lastTransactionQuery = null;
      state.isInitialized = false;
      // Reset all status to idle
      state.billingStatus = 'idle';
      state.invoiceStatus = 'idle';
      state.transactionStatus = 'idle';
      state.paymentMethodStatus = 'idle';
      // Clear all errors
      state.billingError = null;
      state.invoiceError = null;
      state.transactionError = null;
      state.paymentMethodError = null;
    },
    setBillingQuery: (state, action) => {
      state.lastBillingQuery = action.payload;
    },
    setInvoiceQuery: (state, action) => {
      state.lastInvoiceQuery = action.payload;
    },
    setTransactionQuery: (state, action) => {
      state.lastTransactionQuery = action.payload;
    },
    markBillingInitialized: (state) => {
      state.isInitialized = true;
    },
    clearInvoiceDetail: (state) => {
      state.invoiceDetail = null;
      state.invoiceDetailStatus = 'idle';
      state.invoiceDetailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout action
      .addCase(meLogOut.fulfilled, (state) => {
        state.billingAccounts = {};
        state.billingStats = null;
        state.invoices = {};
        state.transactions = {};
        state.transactionStats = null;
        state.paymentMethods = {};
        state.lastBillingQuery = null;
        state.lastInvoiceQuery = null;
        state.lastTransactionQuery = null;
        state.isInitialized = false;
        state.billingStatus = 'idle';
        state.invoiceStatus = 'idle';
        state.transactionStatus = 'idle';
        state.paymentMethodStatus = 'idle';
        state.billingError = null;
        state.invoiceError = null;
        state.transactionError = null;
        state.paymentMethodError = null;
      })

      // Billing Management
      .addCase(getBilling.pending, (state) => {
        state.billingStatus = 'loading';
        state.billingError = null;
      })
      .addCase(getBilling.fulfilled, (state, action) => {
        state.billingStatus = 'succeeded';
        const companyId =
          action.meta.arg?.urlVarsReplaceMap?.['{company}'] || action.meta.arg?.urlVarsReplaceMap?.['{companySlug}'];
        if (companyId) {
          state.billingAccounts[companyId] = action.payload;
        }
      })
      .addCase(getBilling.rejected, (state, action) => {
        state.billingStatus = 'failed';
        state.billingError = action.error.message || 'Failed to load billing information';
      })
      .addCase(updateBilling.fulfilled, (state, action) => {
        const companyId =
          action.meta.arg?.urlVarsReplaceMap?.['{company}'] || action.meta.arg?.urlVarsReplaceMap?.['{companySlug}'];
        if (companyId) {
          state.billingAccounts[companyId] = action.payload;
        }
      })
      .addCase(getBillingStats.fulfilled, (state, action) => {
        state.billingStats = action.payload;
      })

      // Invoice Management
      .addCase(getInvoices.pending, (state) => {
        state.invoiceStatus = 'loading';
        state.invoiceError = null;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.invoiceStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((invoice: InvoiceResource) => {
            state.invoices[invoice.uuid] = invoice;
          });
        }
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.invoiceStatus = 'failed';
        state.invoiceError = action.error.message || 'Failed to load invoices';
      })
      // Handle the alternative invoice show endpoint
      .addCase(getInvoicesShow.pending, (state) => {
        state.invoiceStatus = 'loading';
        state.invoiceError = null;
      })
      .addCase(getInvoicesShow.fulfilled, (state, action) => {
        state.invoiceStatus = 'succeeded';
        // Clear existing invoices before adding new ones
        state.invoices = {};
        // The response has { data: [...] } structure
        const invoicesArray = action.payload?.data;
        if (invoicesArray && Array.isArray(invoicesArray)) {
          invoicesArray.forEach(
            (invoice: {
              id?: string | number;
              uuid?: string;
              invoice_number: string;
              issuing_company_slug: string;
              invoice_status_type?: { slug: string };
              total_amount?: string;
              tax_amount?: string;
              grand_total_amount?: string;
              currency_code?: string;
              issue_date: string;
              due_date: string;
              paid_at?: string | null;
              items?: Array<{
                id?: string | number;
                description?: string;
                quantity?: number;
                unit_price?: string;
                total_price?: string;
                tax_rate_percent?: number;
              }>;
              notes_to_recipient?: string;
              internal_notes?: string;
              reference_number?: string;
              recipient_company_slug?: string;
              display_total_amount?: string;
              display_tax_amount?: string;
              display_grand_total_amount?: string;
              currency_symbol?: string;
              payment_term?: { days?: number };
              created_at: string;
              updated_at: string;
            }) => {
              // Map the response format to our InvoiceResource format
              const invoiceId = invoice.id?.toString() || invoice.uuid;
              if (!invoiceId) {
                console.warn('Invoice missing ID, skipping:', invoice);
                return;
              }

              const mappedInvoice: InvoiceResource = {
                uuid: invoiceId,
                invoiceNumber: invoice.invoice_number,
                companyUuid: invoice.issuing_company_slug,
                status: (invoice.invoice_status_type?.slug as InvoiceResource['status']) || 'draft',
                subtotal: parseFloat(invoice.total_amount || '0'),
                taxAmount: parseFloat(invoice.tax_amount || '0'),
                total: parseFloat(invoice.grand_total_amount || '0'),
                currency: invoice.currency_code || 'GBP',
                issueDate: invoice.issue_date,
                dueDate: invoice.due_date,
                paidAt: invoice.paid_at || undefined,
                paymentMethod: undefined,
                items:
                  invoice.items?.map((item) => ({
                    id: item.id?.toString() || '',
                    description: item.description || '',
                    quantity: item.quantity || 0,
                    unitPrice: parseFloat(item.unit_price || '0'),
                    total: parseFloat(item.total_price || '0'),
                    taxRate: item.tax_rate_percent || 0,
                  })) || [],
                notes: invoice.notes_to_recipient || '',
                metadata: {
                  internalNotes: invoice.internal_notes,
                  referenceNumber: invoice.reference_number,
                  recipientCompanySlug: invoice.recipient_company_slug,
                  displayTotalAmount: invoice.display_total_amount,
                  displayTaxAmount: invoice.display_tax_amount,
                  displayGrandTotalAmount: invoice.display_grand_total_amount,
                  currencySymbol: invoice.currency_symbol,
                  paymentTermDays: invoice.payment_term?.days,
                },
                createdAt: invoice.created_at,
                updatedAt: invoice.updated_at,
              };
              state.invoices[invoiceId] = mappedInvoice;
            },
          );
        }
      })
      .addCase(getInvoicesShow.rejected, (state, action) => {
        state.invoiceStatus = 'failed';
        state.invoiceError = action.error.message || 'Failed to load invoices';
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.invoices[action.payload.uuid] = action.payload;
        }
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.invoices[action.payload.uuid] = action.payload;
        }
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.invoices[action.payload.uuid] = action.payload;
        }
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        const invoiceId = action.meta.arg?.urlVarsReplaceMap?.['{invoice}'];
        if (invoiceId) {
          delete state.invoices[invoiceId];
        }
      })

      // Invoice Detail Management
      .addCase(getInvoiceDetail.pending, (state) => {
        state.invoiceDetailStatus = 'loading';
        state.invoiceDetailError = null;
      })
      .addCase(getInvoiceDetail.fulfilled, (state, action) => {
        state.invoiceDetailStatus = 'succeeded';
        state.invoiceDetail = action.payload;
      })
      .addCase(getInvoiceDetail.rejected, (state, action) => {
        state.invoiceDetailStatus = 'failed';
        const errorPayload = action.payload as { error?: { message?: string } };
        state.invoiceDetailError = errorPayload?.error?.message || 'Failed to load invoice details';
      })

      // Transaction Management
      .addCase(getTransactions.pending, (state) => {
        state.transactionStatus = 'loading';
        state.transactionError = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.transactionStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((transaction: TransactionResource) => {
            state.transactions[transaction.uuid] = transaction;
          });
        }
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.transactionStatus = 'failed';
        state.transactionError = action.error.message || 'Failed to load transactions';
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.transactions[action.payload.uuid] = action.payload;
        }
      })
      .addCase(getTransaction.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.transactions[action.payload.uuid] = action.payload;
        }
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.transactions[action.payload.uuid] = action.payload;
        }
      })
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        const transactionId = action.meta.arg?.urlVarsReplaceMap?.['{transaction}'];
        if (transactionId) {
          delete state.transactions[transactionId];
        }
      })
      .addCase(getTransactionStats.fulfilled, (state, action) => {
        state.transactionStats = action.payload;
      })

      // Payment Method Management
      .addCase(getPaymentMethods.pending, (state) => {
        state.paymentMethodStatus = 'loading';
        state.paymentMethodError = null;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((method: PaymentMethodResource) => {
            state.paymentMethods[method.uuid] = method;
          });
        }
      })
      .addCase(getPaymentMethods.rejected, (state, action) => {
        state.paymentMethodStatus = 'failed';
        state.paymentMethodError = action.error.message || 'Failed to load payment methods';
      })
      .addCase(createPaymentMethod.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.paymentMethods[action.payload.uuid] = action.payload;
        }
      })
      .addCase(getPaymentMethod.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.paymentMethods[action.payload.uuid] = action.payload;
        }
      })
      .addCase(updatePaymentMethod.fulfilled, (state, action) => {
        if (action.payload?.uuid) {
          state.paymentMethods[action.payload.uuid] = action.payload;
        }
      })
      .addCase(deletePaymentMethod.fulfilled, (state, action) => {
        const methodId = action.meta.arg?.urlVarsReplaceMap?.['{method}'];
        if (methodId) {
          delete state.paymentMethods[methodId];
        }
      })
      .addCase(setDefaultPaymentMethod.fulfilled, (state, action) => {
        const methodId = action.meta.arg?.urlVarsReplaceMap?.['{method}'];
        if (methodId) {
          // Set all methods to non-default
          Object.values(state.paymentMethods).forEach((method) => {
            method.isDefault = false;
          });
          // Set the specified method as default
          if (state.paymentMethods[methodId]) {
            state.paymentMethods[methodId].isDefault = true;
          }
        }
      });
  },
});

export const {
  clearBillingState,
  setBillingQuery,
  setInvoiceQuery,
  setTransactionQuery,
  markBillingInitialized,
  clearInvoiceDetail,
} = billingSlice.actions;

// Selectors
export const selectBillingAccounts = (state: AppState) => state.billing.billingAccounts;
export const selectBillingStatus = (state: AppState) => state.billing.billingStatus;
export const selectBillingError = (state: AppState) => state.billing.billingError;
export const selectBillingStats = (state: AppState) => state.billing.billingStats;

export const selectInvoices = (state: AppState) => state.billing.invoices;
export const selectInvoiceStatus = (state: AppState) => state.billing.invoiceStatus;
export const selectInvoiceError = (state: AppState) => state.billing.invoiceError;
export const selectInvoiceDetail = (state: AppState) => state.billing.invoiceDetail;
export const selectInvoiceDetailStatus = (state: AppState) => state.billing.invoiceDetailStatus;
export const selectInvoiceDetailError = (state: AppState) => state.billing.invoiceDetailError;

export const selectTransactions = (state: AppState) => state.billing.transactions;
export const selectTransactionStatus = (state: AppState) => state.billing.transactionStatus;
export const selectTransactionError = (state: AppState) => state.billing.transactionError;
export const selectTransactionStats = (state: AppState) => state.billing.transactionStats;

export const selectPaymentMethods = (state: AppState) => state.billing.paymentMethods;
export const selectPaymentMethodStatus = (state: AppState) => state.billing.paymentMethodStatus;
export const selectPaymentMethodError = (state: AppState) => state.billing.paymentMethodError;

export const selectLastBillingQuery = (state: AppState) => state.billing.lastBillingQuery;
export const selectLastInvoiceQuery = (state: AppState) => state.billing.lastInvoiceQuery;
export const selectLastTransactionQuery = (state: AppState) => state.billing.lastTransactionQuery;

export const selectIsBillingInitialized = (state: AppState) => state.billing.isInitialized;

// Helper selectors
export const selectCompanyBilling = (companyId: string) => (state: AppState) =>
  state.billing.billingAccounts[companyId];

export const selectInvoiceById = (invoiceId: string) => (state: AppState) => state.billing.invoices[invoiceId];

export const selectInvoicesByCompany = (companyId: string) => (state: AppState) =>
  Object.values(state.billing.invoices).filter((invoice) => invoice.companyUuid === companyId);

export const selectInvoicesByStatus = (status: InvoiceResource['status']) => (state: AppState) =>
  Object.values(state.billing.invoices).filter((invoice) => invoice.status === status);

export const selectTransactionById = (transactionId: string) => (state: AppState) =>
  state.billing.transactions[transactionId];

export const selectTransactionsByCompany = (companyId: string) => (state: AppState) =>
  Object.values(state.billing.transactions).filter((transaction) => transaction.companyUuid === companyId);

export const selectTransactionsByInvoice = (invoiceId: string) => (state: AppState) =>
  Object.values(state.billing.transactions).filter((transaction) => transaction.invoiceUuid === invoiceId);

export const selectPaymentMethodById = (methodId: string) => (state: AppState) =>
  state.billing.paymentMethods[methodId];

export const selectPaymentMethodsByCompany = (companyId: string) => (state: AppState) =>
  Object.values(state.billing.paymentMethods).filter((method) => method.companyUuid === companyId);

export const selectDefaultPaymentMethod = (companyId: string) => (state: AppState) =>
  Object.values(state.billing.paymentMethods).find((method) => method.companyUuid === companyId && method.isDefault);

export const selectActivePaymentMethods = (companyId: string) => (state: AppState) =>
  Object.values(state.billing.paymentMethods).filter((method) => method.companyUuid === companyId && method.isActive);

// Load initial billing data action
export const loadInitialBillingData = () => (dispatch: AppDispatch, getState: () => AppState) => {
  const state = getState();

  // Only load if user has admin role for a company
  const userRoles = state.me?.roles || [];
  const hasAdminRole = userRoles.some((role) => role.includes('admin'));
  const currentCompany = state.me?.currentCompanySlug;

  if (hasAdminRole && currentCompany) {
    dispatch(getBilling());
    dispatch(getBillingStats());
    dispatch(getPaymentMethods());
    dispatch(markBillingInitialized());
  }
};

export default billingSlice.reducer;
